import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaSearch, FaMapMarkerAlt, FaIdCard, FaStore, FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { successAlert, errorAlert } from '../utils/Alerts';

const StoreList = ({ theme }) => {
  const [stores,     setStores]     = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading,    setLoading]    = useState(true);

  const isDark   = theme === 'dark';
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/stores`);
        setStores(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error cargando tiendas:', err);
        setStores([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  // Filtro local: nombre, RIF o dirección
  const term = searchTerm.trim().toLowerCase();
  const filtered = stores.filter(s =>
    !term ||
    (s.storeName    || '').toLowerCase().includes(term) ||
    (s.storeRif     || '').toLowerCase().includes(term) ||
    (s.storeAddress || '').toLowerCase().includes(term)
  );

  const handleCopyUrl = async (e, slug) => {
    e.stopPropagation(); // evita que se abra la tienda al hacer clic en copiar
    const url = `${window.location.origin}/creador/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      successAlert({ text: 'URL copiada al portapapeles', timer: 1500 });
    } catch {
      errorAlert({ title: 'Error', text: 'No se pudo copiar la URL' });
    }
  };

  return (
    <MainContainer active={isDark}>
      <TopBar>
        <Header active={isDark}>Tiendas</Header>
      </TopBar>

      <SearchBarContainer>
        <InputWrapper>
          <SearchInput
            active={isDark}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar tienda por nombre, RIF o dirección..."
          />
          <IconButton active={isDark} type="button">
            <FaSearch />
          </IconButton>
        </InputWrapper>
      </SearchBarContainer>

      {loading ? (
        <LoadingContainer>
          <Spinner />
          <LoadingText active={isDark}>Cargando tiendas...</LoadingText>
        </LoadingContainer>
      ) : filtered.length === 0 ? (
        <EmptyMessage active={isDark}>No se encontraron tiendas.</EmptyMessage>
      ) : (
        <StoreGrid>
          {filtered.map(s => (
            <StoreCard
              key={s._id}
              active={isDark}
              onClick={() => navigate(`/creador/${s.storeSlug}`)}
            >
              <CardTop>
                {s.storeLogo ? (
                  <Logo src={s.storeLogo} alt={s.storeName} />
                ) : (
                  <LogoPlaceholder active={isDark}>
                    {(s.storeName || '?').charAt(0).toUpperCase()}
                  </LogoPlaceholder>
                )}
                <StoreName active={isDark}>{s.storeName || 'Sin nombre'}</StoreName>
              </CardTop>

              <InfoList>
                <InfoRow active={isDark}>
                  <FaIdCard />
                  <span>{s.storeRif || 'RIF no registrado'}</span>
                </InfoRow>
                <InfoRow active={isDark}>
                  <FaMapMarkerAlt />
                  <span>{s.storeAddress || 'Dirección no registrada'}</span>
                </InfoRow>
              </InfoList>

              <CardFooter active={isDark}>
                <StoreSlug active={isDark}>/creador/{s.storeSlug}</StoreSlug>
                <BtnGroup>
                  {s.storeSlug && (
                    <CopyBtn
                      type="button"
                      active={isDark}
                      onClick={(e) => handleCopyUrl(e, s.storeSlug)}
                      title="Copiar URL de la tienda"
                    >
                      <FaCopy /> Copiar
                    </CopyBtn>
                  )}
                  <VisitBtn type="button">
                    <FaStore /> Ver tienda
                  </VisitBtn>
                </BtnGroup>
              </CardFooter>
            </StoreCard>
          ))}
        </StoreGrid>
      )}
    </MainContainer>
  );
};

/* ═══════════ STYLED COMPONENTS ═══════════ */

const MainContainer = styled.div`
  background-color: ${p => p.active ? '#000' : '#fff'};
  min-height: 100%;
  padding: 40px;
  transition: background-color 0.3s;
  @media(max-width:768px){ padding: 20px; }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 12px;
`;

const Header = styled.h2`
  margin: 0;
  font-weight: 700;
  font-size: 1.4rem;
  color: ${p => p.active ? '#fff' : '#111'};
`;

const SearchBarContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 460px;
  display: flex;
  align-items: center;
  @media(max-width:768px){ width: 100%; }
`;

const SearchInput = styled.input`
  width: 100%;
  background: ${p => p.active ? '#1a1a1a' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#ddd'};
  border-radius: 25px;
  padding: 12px 50px 12px 20px;
  font-size: 15px;
  color: ${p => p.active ? '#e0e0e0' : '#333'};
  outline: none;
  transition: all 0.2s;
  &::placeholder { color: ${p => p.active ? '#555' : '#bbb'}; }
  &:focus { border-color: ${p => p.active ? '#444' : '#4f46e5'}; }
`;

const IconButton = styled.button`
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: ${p => p.active ? '#555' : '#bbb'};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: ${p => p.active ? '#555' : '#bbb'};
  font-size: 15px;
`;

const StoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 20px;
`;

const StoreCard = styled.div`
  background: ${p => p.active ? '#141414' : '#fff'};
  border: 1px solid ${p => p.active ? '#222' : '#e8e8e8'};
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 10px ${p => p.active ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)'};
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 16px;
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(168, 85, 247, 0.5);
    box-shadow: 0 8px 20px ${p => p.active ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.12)'};
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(128,128,128,0.25);
`;

const LogoPlaceholder = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  background: ${p => p.active ? '#252525' : '#e8e8e8'};
  color: ${p => p.active ? '#fff' : '#555'};
`;

const StoreName = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${p => p.active ? '#fff' : '#111'};
  word-break: break-word;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.86rem;
  line-height: 1.4;
  color: ${p => p.active ? '#999' : '#666'};
  svg { flex-shrink: 0; margin-top: 3px; color: #a855f7; }
  span { word-break: break-word; }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-top: 1px solid ${p => p.active ? '#222' : '#f0f0f0'};
  padding-top: 12px;
  margin-top: auto;
`;

const StoreSlug = styled.span`
  font-size: 11px;
  color: ${p => p.active ? '#555' : '#bbb'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VisitBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
  border: none;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border: 4px solid rgba(79, 70, 229, 0.2);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoadingText = styled.p`
  color: ${p => p.active ? '#666' : '#999'};
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

const BtnGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.active ? 'rgba(255,255,255,0.06)' : '#fff'};
  border: 1px solid ${p => p.active ? '#333' : '#ddd'};
  color: ${p => p.active ? '#ccc' : '#555'};
  transition: all 0.2s;
  &:hover {
    border-color: rgba(79,70,229,0.5);
    color: ${p => p.active ? '#818cf8' : '#4f46e5'};
    background: rgba(79,70,229,0.08);
  }
`;

export default StoreList;